/*
 * Cart-drawer coupons — "Apply" behaviour.
 *
 * A single delegated click listener (so it survives the drawer's server-side
 * re-renders) applies a coupon code to the session via /discount/CODE, verifies
 * it actually stuck by reading /cart.js, then refreshes the drawer so the
 * confirmed discount appears in the totals. Invalid/ineligible codes surface an
 * inline message instead.
 *
 * The visible coupon list + its show/hide logic live in snippets/cart-coupons.liquid.
 */
(function () {
  if (window.__onyuCartCoupons) return;
  window.__onyuCartCoupons = true;

  const root = () => (window.shopUrl || '').replace(/\/$/, '');
  const cartUrl = () => (window.routes && window.routes.cart_url) || '/cart';

  function setStatus(scope, message, isError) {
    const el = (scope || document).querySelector('[data-cart-coupons-status]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
    el.classList.toggle('cart-coupons__status--error', !!isError);
  }

  function isApplied(cart, code) {
    const lc = String(code).toLowerCase();
    if (cart && Array.isArray(cart.discount_codes)) {
      const found = cart.discount_codes.find(function (d) {
        return d && String(d.code).toLowerCase() === lc;
      });
      if (found) return !!found.applicable;
    }
    // Fallbacks for cart.js payloads that don't expose discount_codes.
    if (cart && Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length) {
      return true;
    }
    return !!(cart && typeof cart.total_discount === 'number' && cart.total_discount > 0);
  }

  async function refreshDrawer() {
    const res = await fetch(`${cartUrl()}?section_id=cart-drawer`);
    const text = await res.text();
    const html = new DOMParser().parseFromString(text, 'text/html');
    ['cart-drawer-items', '.cart-drawer__footer'].forEach(function (selector) {
      const current = document.querySelector(selector);
      const next = html.querySelector(selector);
      if (current && next) current.replaceWith(next);
    });
  }

  async function applyCoupon(button) {
    const code = button.getAttribute('data-coupon-code');
    if (!code || button.dataset.loading === 'true') return;

    const scope = button.closest('[data-cart-coupons]');
    const label = button.querySelector('.cart-coupon__apply-label');
    const original = label ? label.textContent : '';
    button.dataset.loading = 'true';
    button.disabled = true;
    if (label) label.textContent = 'Applying…';
    setStatus(scope, '');

    try {
      // 1) Apply the code to the session.
      await fetch(`${root()}/discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        credentials: 'same-origin',
      });

      // 2) Confirm it actually applies to this cart.
      const cartRes = await fetch(`${cartUrl()}.js`, { credentials: 'same-origin' });
      const cart = await cartRes.json();

      if (isApplied(cart, code)) {
        // 3) Re-render the drawer; the confirmed discount now shows in the totals.
        await refreshDrawer();
      } else {
        if (label) label.textContent = original;
        button.disabled = false;
        button.dataset.loading = 'false';
        setStatus(scope, "That code isn't valid for your cart.", true);
      }
    } catch (e) {
      if (label) label.textContent = original;
      button.disabled = false;
      button.dataset.loading = 'false';
      setStatus(scope, "Couldn't apply the code. Please try again.", true);
    }
  }

  // Coupons sheet open/closed. The drawer swaps its footer HTML on every cart
  // change (cart.js, cart-drawer.js, refreshDrawer above), which would drop the
  // open sheet, so the state is kept here and re-applied after each swap.
  let couponsOpen = false;

  function syncCoupons() {
    document.querySelectorAll('[data-cart-coupons-toggle]').forEach(function (toggle) {
      const sheet = document.getElementById(toggle.getAttribute('aria-controls'));
      toggle.setAttribute('aria-expanded', couponsOpen ? 'true' : 'false');
      if (!sheet) return;
      sheet.hidden = !couponsOpen;
      sheet.classList.toggle('is-open', couponsOpen);
    });
  }

  function setCouponsOpen(open) {
    couponsOpen = open;
    const toggle = document.querySelector('[data-cart-coupons-toggle]');
    const sheet = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
    if (!sheet) return;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      // Unhide first, then add the class on the next frame so it slides in.
      sheet.hidden = false;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          sheet.classList.add('is-open');
        });
      });
      const back = sheet.querySelector('[data-cart-coupons-close]');
      if (back) back.focus({ preventScroll: true });
    } else {
      sheet.classList.remove('is-open');
      sheet.hidden = true;
      toggle.focus({ preventScroll: true });
    }
  }

  function closeShippingInfo(except) {
    document.querySelectorAll('[data-cart-info].is-open').forEach(function (info) {
      if (info === except) return;
      info.classList.remove('is-open');
      const btn = info.querySelector('button');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  }

  const drawer = document.querySelector('cart-drawer') || document.body;
  new MutationObserver(syncCoupons).observe(drawer, { childList: true, subtree: true });
  // Closing the drawer resets it, so it reopens on the cart view.
  new MutationObserver(function () {
    if (couponsOpen && !drawer.classList.contains('active')) {
      couponsOpen = false;
      syncCoupons();
    }
  }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  syncCoupons();

  document.addEventListener('click', function (event) {
    const button = event.target.closest('.cart-coupon__apply');
    if (button) {
      event.preventDefault();
      applyCoupon(button);
      return;
    }

    if (event.target.closest('[data-cart-coupons-toggle]')) {
      setCouponsOpen(true);
      return;
    }

    if (event.target.closest('[data-cart-coupons-close]')) {
      setCouponsOpen(false);
      return;
    }

    // Free-shipping "i": hover/focus open it via CSS; a tap toggles it so it
    // also works on touch screens. Any outside tap closes it.
    const info = event.target.closest('[data-cart-info]');
    if (info && event.target.closest('.cart-info__button')) {
      const open = !info.classList.contains('is-open');
      closeShippingInfo(info);
      info.classList.toggle('is-open', open);
      event.target.closest('button').setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }
    if (!info) closeShippingInfo();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeShippingInfo();
  });

  // Escape inside the open sheet goes back to the cart instead of closing the
  // whole drawer (cart-drawer.js closes on keyup Escape, in the bubble phase).
  document.addEventListener(
    'keyup',
    function (event) {
      if (event.code !== 'Escape' || !couponsOpen) return;
      event.stopPropagation();
      setCouponsOpen(false);
    },
    true
  );
})();
